import { useMemo } from 'react';

import { buildVisibilityFilter } from 'menu-items/catalogueVisibility';
import { MENU_CATALOGUE_ROUTES } from 'menu-items/catalogueRoutes';

import useCatalogueVisibility from './useCatalogueVisibility';
import useMyMenu from './useMyMenu';

// ==============================|| HOOK - EFFECTIVE CATALOGUE HIDING ||============================== //

/**
 * One "is this switched off FOR ME" filter, combining the two SUBTRACTIVE gates a
 * screen must respect:
 *
 *   1. the GLOBAL Features and Pages switch (`useCatalogueVisibility`) — applies to
 *      everyone; and
 *   2. the PER-ROLE narrowing from Role Definitions (`useMyMenu().denied_pages`) — the
 *      pages this user's role(s) were explicitly un-ticked for.
 *
 * Both are expressed as disabled catalogue pages, so they feed the SAME
 * `buildVisibilityFilter` and every consumer — a tab list, a session-sheet picker —
 * gets both at once. This is why un-ticking a page that is only a TAB (a child's
 * Settings/Mastery tab, a data sheet) can be honoured at all: a route guard cannot
 * hide a tab that shares its page's URL, but `codeHidden(pageCode)` can.
 *
 * PER-ROLE DENIALS CAN ONLY HIDE. The server lists a page in `denied_pages` only when
 * an admin un-ticked it AND no role the user still holds grants it, so folding them in
 * here never widens access — it matches the Role Definitions rule that grants narrow a
 * role's defaults and never add to them.
 *
 * Returns null when nothing is known yet (fail open) — blanking a screen on a pending
 * lookup is worse than briefly showing a tab, and the API refuses the data anyway.
 * Callers MUST treat null as "hide nothing", exactly as they did with the bare
 * `buildVisibilityFilter(visibility)` this replaces.
 */
export default function useHiddenCatalogue() {
  const visibility = useCatalogueVisibility();
  const myMenu = useMyMenu();

  return useMemo(() => {
    const denied = myMenu?.denied_pages || [];
    // Nothing known and nothing denied → fail open, same as buildVisibilityFilter(null).
    if (!visibility && !denied.length) return null;
    return buildVisibilityFilter(
      {
        disabled_features: visibility?.disabled_features || [],
        disabled_pages: [...(visibility?.disabled_pages || []), ...denied]
      },
      MENU_CATALOGUE_ROUTES
    );
  }, [visibility, myMenu]);
}
